import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxArchive,
  faFileCode,
  faPaperPlane,
  faPlus,
  faSignature,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService, { PublicationFileKind } from "api/localLawService";
import pluginService from "api/pluginService";
import { axiosInstance } from "api/axios";
import { ICertificate } from "dto/ICertificate";
import {
  EPublicationRequestStatus,
  IPublicationRequest,
} from "dto/LocalLaw/IPublication";
import { ENotificationType, notification } from "utils/notification";

import PublicationWizard from "./PublicationWizard";
import styles from "./styles.module.scss";

// Wnioski o ogłoszenie aktów w wojewódzkim dzienniku urzędowym (UPE).
// Odpowiednik listy wniosków z Magistratu (Kwantum.SL.LocalLaw.SentRequestsList)
// wraz z czynnościami, które tam były rozsypane po krokach kreatora: podpis
// (ReadyToSendPage) i wysyłka (SendingPage).
//
// Podpis składa wtyczka na stanowisku — certyfikat kwalifikowany nigdy nie trafia
// na serwer. Serwer wydaje XML wniosku, wtyczka odsyła go z podpisem XAdES.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const formatDate = (iso?: string | null) =>
  iso ? moment(iso).format("DD.MM.YYYY") : "—";

const badgeFor = (status: EPublicationRequestStatus) => {
  if (status === EPublicationRequestStatus.Sent) return styles.badgeBip;
  if (status === EPublicationRequestStatus.Error) return styles.badgeWarn;
  return styles.badgeStatus;
};

const RequestsView = () => {
  const requestsQuery = localLawService.useGetPublicationRequests();
  const { mutateAsync: send, isLoading: isSending } =
    localLawService.useSendPublicationRequest();

  const { data: certsData } = pluginService.useGetCerificates();
  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const [creating, setCreating] = useState(false);
  const [signingFor, setSigningFor] = useState<IPublicationRequest | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const requests = requestsQuery.data?.data ?? [];

  const download = async (id: string, kind: PublicationFileKind) => {
    try {
      const { data } = await localLawService.getRequestFileUrl(id, kind);

      if (data.Uri) window.open(data.Uri, "_blank", "noopener");
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: errorText(e, "Nie udało się pobrać pliku wniosku."),
      });
    }
  };

  /**
   * Podpis wniosku: serwer wydaje XML, wtyczka podpisuje go XAdES na stanowisku,
   * serwer wstawia podpisaną wersję do paczki archiwalnej.
   */
  const sign = async (request: IPublicationRequest, cert: ICertificate) => {
    setSigningFor(null);
    setBusyId(request.Id);

    try {
      const payload = await axiosInstance.get(
        `locallaw/publication/requests/${request.Id}/signing-payload`,
      );

      const signed = await pluginService.signDocument({
        CertificateSerialNumber: cert.SerialNumber,
        Content: payload.data.Content,
        ContentType: "text/xml",
        FileName: payload.data.FileName ?? "Wniosek.xml",
        GenerateReport: false,
        ForceXades: true,
      });

      const content = (signed.data as { Content?: string })?.Content;

      if (!content) throw new Error("Wtyczka nie zwróciła podpisanego pliku.");

      await axiosInstance.post(
        `locallaw/publication/requests/${request.Id}/signed`,
        { Content: content },
      );

      notification({
        type: ENotificationType.SUCCESS,
        title: "Wniosek podpisany",
        text: "Możesz go teraz wysłać do UPE.",
      });

      requestsQuery.refetch();
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: errorText(
          e,
          (e as Error)?.message ?? "Nie udało się podpisać wniosku.",
        ),
      });
    } finally {
      setBusyId(null);
    }
  };

  const dispatch = async (request: IPublicationRequest) => {
    setBusyId(request.Id);

    try {
      await send(request.Id);

      notification({
        type: ENotificationType.SUCCESS,
        title: "Wniosek wysłany",
        text: "Paczka trafiła do UPE.",
      });

      requestsQuery.refetch();
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: errorText(e, "Nie udało się wysłać wniosku."),
      });
    } finally {
      setBusyId(null);
    }
  };

  if (requestsQuery.isLoading) return <Loading />;

  return (
    <>
      <div className={styles.topBar}>
        <span className={styles.listHeading}>
          Wnioski o publikację ({requests.length})
        </span>

        <div className={styles.spacer} />

        <Button
          text="Nowy wniosek"
          element={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => setCreating(true)}
        />
      </div>

      <div className={styles.listWrap}>
        {requests.map((request) => (
          <div key={request.Id} className={styles.actRow}>
            <div className={styles.actMain}>
              <span className={styles.actTitle}>
                {request.Signature
                  ? `${request.TypeName} nr ${request.Signature}`
                  : request.TypeName}
              </span>

              <span className={styles.actMeta}>
                {formatDate(request.RequestDate)} · adresat:{" "}
                {request.RecipientName ?? "—"} · aktów: {request.Items.length}
                {request.UpeIssueId ? ` · sprawa UPE: ${request.UpeIssueId}` : ""}
              </span>

              {request.Items.map((item) => (
                <span key={item.LegalActId} className={styles.actMeta}>
                  •{" "}
                  {item.ActNumber
                    ? `${item.ActTypeName} Nr ${item.ActNumber}`
                    : item.ActTypeName}{" "}
                  — {item.Title}
                </span>
              ))}

              {request.LastError && (
                <span className={styles.actComment}>{request.LastError}</span>
              )}

              <div className={styles.badges}>
                <span className={`${styles.badge} ${badgeFor(request.Status)}`}>
                  {request.StatusName}
                </span>
                {request.IsSigned && (
                  <span className={styles.badge}>
                    podpisany {formatDate(request.SignedDate)}
                  </span>
                )}
                {request.SentDate && (
                  <span className={styles.badge}>
                    wysłany {formatDate(request.SentDate)}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.rowTools}>
              <button
                className={styles.toolBtn}
                title="Pobierz XML wniosku"
                onClick={() => download(request.Id, "xml")}
              >
                <FontAwesomeIcon icon={faFileCode} />
              </button>

              <button
                className={styles.toolBtn}
                title="Pobierz paczkę wniosku"
                disabled={!request.HasPackage}
                onClick={() => download(request.Id, "package")}
              >
                <FontAwesomeIcon icon={faBoxArchive} />
              </button>

              <button
                className={styles.toolBtn}
                title="Podpisz wniosek certyfikatem kwalifikowanym"
                disabled={
                  busyId === request.Id ||
                  request.Status === EPublicationRequestStatus.Sent
                }
                onClick={() => setSigningFor(request)}
              >
                <FontAwesomeIcon icon={faSignature} />
              </button>

              <button
                className={styles.toolBtn}
                title="Wyślij wniosek do UPE"
                disabled={
                  !request.IsSigned ||
                  isSending ||
                  busyId === request.Id ||
                  request.Status === EPublicationRequestStatus.Sent
                }
                onClick={() => dispatch(request)}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </div>
        ))}

        {requests.length === 0 && (
          <div className={styles.emptyList}>
            Nie złożono jeszcze żadnego wniosku o publikację.
          </div>
        )}
      </div>

      {creating && (
        <PublicationWizard
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            requestsQuery.refetch();
          }}
        />
      )}

      {signingFor && (
        <div className={styles.overlay} onMouseDown={() => setSigningFor(null)}>
          <div
            className={styles.modal}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={styles.modalTitle}>Wybierz certyfikat</div>

            <div className={styles.modalBody}>
              {certs.length === 0 ? (
                <div className={styles.hint}>
                  Brak dostępnych certyfikatów. Uruchom i połącz wtyczkę Nota oraz
                  podłącz kartę z certyfikatem kwalifikowanym.
                </div>
              ) : (
                certs.map((cert) => (
                  <button
                    key={cert.SerialNumber}
                    className={styles.pickRow}
                    onClick={() => sign(signingFor, cert)}
                  >
                    <span>
                      {cert.Name}
                      <div className={styles.hint}>
                        {cert.IssuerBy} · ważny do{" "}
                        {moment(cert.ValidTo).format("DD.MM.YYYY")}
                      </div>
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className={styles.modalFooter}>
              <HollowButton text="Anuluj" onClick={() => setSigningFor(null)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestsView;
